import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AccountType,
  DocumentStatus,
  PermissionEnum,
  TransactionTypeEnum,
  DocumentType,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetAccountBalanceByIdQuery,
  useLazyGetGeneralLedgerStatmentQuery,
  useLazyGetStatementChangeLogsQuery,
  useAddStatementDocumentToGLTransactionMutation,
  useDeleteStatementsGLDocumentsMutation,
  useLazyGetStatementsGLDocumentQuery,
  useLazyGetStatementsGLDocumentsQuery,
  useCheckStatementDocumentStringNameMutation,
  useStatementsUploadMutation,
  useLazyGetStatementsDocumentFileQuery,
  useChangeStatementsDocumentStatusMutation,
  useChangeStatementsDocumentCategoryMutation,
  useAddStatementsCommentMutation,
  useEditStatementsCommentMutation,
  useDeleteStatementsCommentMutation,
} from '../../store/services/item';
import { GeneralTransactionState } from '../../store/slices/gl-transaction';
import { UploadResponse } from '../../types';

const useGetGLFunctions = ({
  transactionTypes,
  gl,
  transactionId,
  activePermissions,
}: {
  transactionTypes?: {
    name: string;
    id: string;
  }[];
  gl: GeneralTransactionState;
  transactionId?: string;
  activePermissions?: PermissionEnum[];
}) => {
  const [attachedDocumentsErrors, setAttachedDocumentsErrors] = useState<
    | Record<
        string,
        {
          name: string;
          error: string;
        }
      >
    | undefined
  >(undefined);
  const [uploadedDocumentsErrors, setUploadedDocumentsErrors] = useState<
    { name: string; error: string }[]
  >([]);
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [imgLink, setImgLink] = useState('');

  const { document: documentItem, documents } = useAppSelector(
    (state) => state.accountsStatements.transactions.gl,
  );

  const [getGeneralLedger, { isLoading, isFetching }] =
    useLazyGetGeneralLedgerStatmentQuery();

  const [
    getDebitAccountBalanceById,
    { isLoading: debitBalanceIsLoading, isFetching: debitBalanceIsFetching },
  ] = useLazyGetAccountBalanceByIdQuery();
  const [
    getCreditAccountBalanceById,
    { isLoading: creditBalanceIsLoading, isFetching: creditBalanceIsFetching },
  ] = useLazyGetAccountBalanceByIdQuery();
  const [
    getTransactionChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetStatementChangeLogsQuery();

  const [
    getTransactionDocuments,
    { isLoading: documentsIsLoading, isFetching: documentsIsFetching },
  ] = useLazyGetStatementsGLDocumentsQuery();
  const [
    addDocumentsToTransaction,
    { isLoading: addDocumentInProgress, isSuccess: fileUploadIsSuccess },
  ] = useAddStatementDocumentToGLTransactionMutation();
  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckStatementDocumentStringNameMutation();
  const [upload, { isLoading: fileUploadInProgress }] =
    useStatementsUploadMutation();
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetStatementsGLDocumentQuery();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetStatementsDocumentFileQuery();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteStatementsGLDocumentsMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeStatementsDocumentStatusMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeStatementsDocumentCategoryMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddStatementsCommentMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditStatementsCommentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteStatementsCommentMutation();

  const getTransaction = useCallback((id: string) => {
    getGeneralLedger(id);
  }, []);

  const getTransactionChangeLogsAction = useCallback(
    (cursor?: string) => {
      getTransactionChangeLogs(
        `?take=20&entityType=GENERAL_LEDGER_TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const linkedTransactionPath = useMemo(() => {
    const currentTransactionType = transactionTypes?.find(
      (item) => item.id === gl?.transaction?.transaction?.typeId,
    );

    let path = '';

    if (currentTransactionType) {
      let link = '#';

      if (
        currentTransactionType?.name === TransactionTypeEnum.INTERNAL &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST)
      ) {
        link = '/transactions/internal';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INCOMING &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INCOMING_LIST)
      ) {
        link = '/transactions/incoming';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.OUTGOING &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_OUTGOING_LIST)
      ) {
        link = '/transactions/outgoing';
      } else if (
        currentTransactionType?.name ===
          TransactionTypeEnum.CURRENCY_EXCHANGE &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_EXCHANGE_LIST)
      ) {
        link = '/transactions/exchange';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INTERNAL_PAYMENT &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST)
      ) {
        link = '/transactions/internal';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.NOSTRO &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_NOSTRO_LIST)
      ) {
        link = '/transactions/nostro';
      } else if (
        currentTransactionType?.name === 'FEE' &&
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_LIST,
        )
      ) {
        link = '/transactions/general-ledger/service-fee';
      }

      path = `${link}/${gl.transaction?.parentTransactionId}`;
    }

    return path;
  }, [
    transactionTypes,
    gl?.transaction?.transaction?.typeId,
    gl.transaction?.parentTransactionId,
  ]);

  const getTransationDocumentsAction = (cursor?: string) => {
    const body: {
      id: string;
      cursor?: string;
    } = {
      id: transactionId as string,
    };

    if (cursor) {
      body.cursor = cursor;
    }

    getTransactionDocuments(body);
  };

  const onUploadDocument = async (files: FileList, organizationId?: string) => {
    setAttachedDocumentsErrors(undefined);
    setUploadedDocumentsErrors([]);

    if (setUploadedFilesError) {
      setUploadedFilesError([]);
    }
    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const checkNameResponse = (await checkDocumentName({
          value: fileItem.name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = fileItem?.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    setUploadedDocumentsErrors(nextUploadedDocumentsErrors);

    if (setUploadedFilesError) {
      setUploadedFilesError(nextUploadedFilesErrors);
    }

    if (filesResponse && filesResponse.length) {
      const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

      const uploadedFiles: { name: string; uploadId: string }[] = [];

      filesResponse.forEach((item) => {
        if (item && item.name && item.id) {
          uploadedFiles.push({
            name: item?.name,
            uploadId: item?.id,
          });
        }
      });

      if (uploadedFiles.length) {
        await Promise.all(
          uploadedFiles.map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              organizationId?: string;
            } = {
              uploadId: item.uploadId,
              name: item.name,
            };

            if (organizationId) {
              body.organizationId = organizationId;
            }

            const response = (await addDocumentsToTransaction({
              id: transactionId as string,
              body,
            })) as {
              error?: {
                data: {
                  message?: {
                    constraints: Record<string, string>;
                  }[];
                };
              };
            };

            if (response.error && response?.error?.data?.message?.length) {
              const errorItem = response?.error?.data?.message[0].constraints;

              if (errorItem) {
                nextAttachedDocumentsErrors[item.uploadId] = {
                  name: item.name,
                  error: Object.values(errorItem)[0],
                };
              }
            }

            return response;
          }),
        );

        setAttachedDocumentsErrors(nextAttachedDocumentsErrors);
      }
    }
  };

  const getTransactionDocumentByIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: documentItem?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  useEffect(() => {
    if (transactionId && gl.transaction) {
      getDebitAccountBalanceById({
        id: gl.transaction?.debitAccountId as string,
        query: `&currencyId=${gl.transaction?.currencyId}`,
        typeAcc: AccountType.DEBIT,
      });
      getCreditAccountBalanceById({
        id: gl.transaction?.creditAccountId as string,
        query: `&currencyId=${gl.transaction?.currencyId}`,
        typeAcc: AccountType.CREDIT,
      });
    }
  }, [transactionId, gl.transaction]);

  useEffect(() => {
    if (
      !gl?.transaction &&
      transactionId &&
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_LIST,
      )
    ) {
      getTransaction(transactionId);
    }
  }, [getTransaction, gl?.transaction, transactionId, activePermissions]);

  return {
    isLoading,
    isFetching,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    linkedTransactionPath,
    getTransactionChangeLogsAction,
    changeLogsIsLoading,
    changeLogsIsFetching,

    getTransationDocumentsAction,
    documentsIsLoading,
    documentsIsFetching,
    attachedDocumentsErrors,
    uploadedDocumentsErrors,
    uploadedFilesError,
    imgLink,
    addDocumentInProgress,
    fileUploadIsSuccess,
    checkDocumentNameInProgress,
    fileUploadInProgress,
    onUploadDocument,
    documentIsLoading,
    documentIsFetching,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    getTransactionDocumentByIdAction,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    onDeleteDocument,
    onChangeCustomerDocumentsStatus,
    documentStatusChangeInProgress,
    documentCategoryChangeInProgress,
    onMoveDocument,
    commentInProgress,
    onSendDocumentComment,
    onEditComment,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteComment,
    deleteCommentInProgress,
    onDownloadDocument,
  };
};

export default useGetGLFunctions;
